/**
 * @zendir/ui - SimulationControls Component
 *
 * Space operations simulation playback controls following Astro UX patterns.
 * Provides play/pause/stop/step controls for simulation time management.
 *
 * Features:
 * - Play/Pause toggle with visual state feedback
 * - Stop (reset) functionality
 * - Step forward/backward controls
 * - Time scale (playback rate) adjustment
 * - Simulation time display integration
 * - AOS/LOS timer integration
 * - Keyboard accessibility
 * - AstroUXDS compliant styling
 *
 * Space Operations Terminology:
 * - SIM: Simulation mode indicator
 * - MET: Mission Elapsed Time
 * - Epoch: Simulation start time (t=0)
 * - Time Scale: Playback speed multiplier
 *
 * @example
 * ```tsx
 * import { SimulationControls } from '@zendir/ui/react';
 *
 * <SimulationControls
 *   isPlaying={isPlaying}
 *   onPlay={handlePlay}
 *   onPause={handlePause}
 *   onStop={handleStop}
 *   timeScale={1}
 *   onTimeScaleChange={setTimeScale}
 *   currentTime={simulationTime}
 *   epoch={epoch}
 * />
 * ```
 */

import React, { memo, useState, useCallback, useMemo } from "react";
import { useTheme } from "../theme";
import { classNames, tabularNumsStyle, safeAccentText } from "../utils";
import { AstroIcon } from "../core/display/AstroIcon";
import { Tooltip } from "../core/overlays/Tooltip";

// ============================================================================
// Types
// ============================================================================

export type SimulationState = "stopped" | "playing" | "paused";

export interface SimulationControlsProps {
  /** Current simulation state */
  state?: SimulationState;
  /** Whether simulation is playing (alternative to state) */
  isPlaying?: boolean;
  /** Whether simulation is paused (used with isPlaying for 3-state) */
  isPaused?: boolean;

  // === Playback Callbacks ===
  /** Called when play is triggered */
  onPlay?: () => void;
  /** Called when pause is triggered */
  onPause?: () => void;
  /** Called when stop (reset) is triggered */
  onStop?: () => void;
  /** Called when step forward is triggered */
  onStepForward?: () => void;
  /** Called when step backward is triggered */
  onStepBackward?: () => void;

  // === Time Display ===
  /** Current simulation time */
  currentTime?: Date | string;
  /** Simulation epoch (t=0) */
  epoch?: Date | string;
  /** Current elapsed seconds from epoch */
  elapsedSeconds?: number;
  /** Show elapsed time display */
  showElapsedTime?: boolean;
  /** Show current time display */
  showCurrentTime?: boolean;
  /** Show date in time display */
  showDate?: boolean;

  // === Time Scale ===
  /** Current time scale factor (1 = realtime) */
  timeScale?: number;
  /** Callback when time scale changes */
  onTimeScaleChange?: (scale: number) => void;
  /** Available time scale presets */
  timeScalePresets?: number[];
  /** Show time scale controls */
  showTimeScale?: boolean;

  // === Step Size ===
  /** Step size in seconds */
  stepSize?: number;
  /** Callback when step size changes */
  onStepSizeChange?: (size: number) => void;
  /** Available step size presets in seconds */
  stepSizePresets?: number[];
  /** Show step controls */
  showStepControls?: boolean;

  // === Appearance ===
  /** Compact display mode */
  compact?: boolean;
  /** Small size variant */
  small?: boolean;
  /** Custom className */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Show simulation status indicator */
  showStatus?: boolean;
  /** Custom label for simulation mode */
  label?: string;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_TIME_SCALE_PRESETS = [0.1, 0.25, 0.5, 1, 2, 5, 10, 60, 3600];
const DEFAULT_STEP_SIZE_PRESETS = [1, 5, 10, 30, 60, 300, 600, 3600];

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format elapsed time as DD:HH:MM:SS
 */
function formatElapsedTime(seconds: number): string {
  const absSeconds = Math.abs(seconds);
  const days = Math.floor(absSeconds / 86400);
  const hours = Math.floor((absSeconds % 86400) / 3600);
  const mins = Math.floor((absSeconds % 3600) / 60);
  const secs = Math.floor(absSeconds % 60);

  const sign = seconds < 0 ? "-" : "";

  if (days > 0) {
    return `${sign}${days}:${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${sign}${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Format time scale for display
 */
function formatTimeScale(scale: number): string {
  if (scale >= 3600) return `${scale / 3600}h/s`;
  if (scale >= 60) return `${scale / 60}m/s`;
  if (scale === 1) return "1×";
  if (scale < 1) return `${scale}×`;
  return `${scale}×`;
}

/**
 * Format step size for display
 */
function formatStepSize(seconds: number): string {
  if (seconds >= 3600) return `${seconds / 3600}h`;
  if (seconds >= 60) return `${seconds / 60}m`;
  return `${seconds}s`;
}

/**
 * Format time for display
 */
function formatTimeDisplay(date: Date): string {
  const hours = date.getUTCHours().toString().padStart(2, "0");
  const mins = date.getUTCMinutes().toString().padStart(2, "0");
  const secs = date.getUTCSeconds().toString().padStart(2, "0");
  return `${hours}:${mins}:${secs}`;
}

/**
 * Format date for display
 */
function formatDateDisplay(date: Date): string {
  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = date.getUTCDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// ============================================================================
// Sub-components
// ============================================================================

interface ControlButtonProps {
  icon: string;
  label: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  danger?: boolean;
  tokens: ReturnType<typeof useTheme>["tokens"];
  small?: boolean;
}

const ControlButton = memo(function ControlButton({
  icon,
  label,
  onClick,
  active = false,
  disabled = false,
  danger = false,
  tokens,
  small = false,
}: ControlButtonProps) {
  const size = small ? 28 : 32;
  const iconSize = small ? 14 : 16;

  const baseColor = danger
    ? tokens.colors.status.critical
    : active
      ? tokens.colors.status.normal
      : tokens.colors.text.primary;

  const bgColor = active
    ? `${tokens.colors.status.normal}25`
    : "rgba(255,255,255,0.14)";

  return (
    <Tooltip content={label} placement="bottom">
      <button
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: size,
          height: size,
          padding: 0,
          border: "none",
          borderRadius: tokens.borderRadius.md,
          backgroundColor: disabled ? "rgba(255,255,255,0.06)" : bgColor,
          color: disabled ? tokens.colors.text.tertiary : baseColor,
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "all 0.15s ease",
          opacity: disabled ? 0.5 : 1,
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.backgroundColor = active
              ? `${tokens.colors.status.normal}35`
              : "rgba(255,255,255,0.22)";
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.currentTarget.style.backgroundColor = bgColor;
          }
        }}
      >
        <AstroIcon
          name={icon as any}
          size={iconSize}
          color={disabled ? tokens.colors.text.tertiary : baseColor}
        />
      </button>
    </Tooltip>
  );
});

interface TimeScaleSelectorProps {
  value: number;
  presets: number[];
  onChange?: (scale: number) => void;
  disabled?: boolean;
  tokens: ReturnType<typeof useTheme>["tokens"];
  small?: boolean;
}

const TimeScaleSelector = memo(function TimeScaleSelector({
  value,
  presets,
  onChange,
  disabled = false,
  tokens,
  small = false,
}: TimeScaleSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={{ position: "relative" }}>
      <Tooltip content="Time Scale / Playback Speed" placement="bottom">
        <button
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: small ? "4px 8px" : "6px 10px",
            border: "none",
            borderRadius: tokens.borderRadius.sm,
            backgroundColor: "rgba(255,255,255,0.08)",
            color:
              value !== 1
                ? tokens.colors.status.caution
                : tokens.colors.text.secondary,
            fontSize: small ? tokens.typography.fontSize.xxs : "0.6875rem", // 10px/11px in rem
            fontWeight: 700, // AstroUXDS bold for time display (critical info)
            fontFamily: tokens.typography.fontFamily.mono,
            cursor: disabled ? "not-allowed" : "pointer",
            transition: "all 0.15s ease",
            opacity: disabled ? 0.5 : 1,
            ...tabularNumsStyle,
          }}
        >
          <AstroIcon
            name="fast-forward"
            size={small ? 10 : 12}
            color="inherit"
          />
          {formatTimeScale(value)}
        </button>
      </Tooltip>

      {isOpen && !disabled && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 99,
            }}
            onClick={() => setIsOpen(false)}
          />
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              marginTop: 4,
              padding: 4,
              backgroundColor: tokens.colors.background.elevated,
              border: `1px solid ${tokens.colors.border.default}`,
              borderRadius: tokens.borderRadius.md,
              boxShadow: tokens.shadows.lg,
              zIndex: 100,
              minWidth: 80,
            }}
          >
            {presets.map((scale) => (
              <button
                key={scale}
                onClick={() => {
                  onChange?.(scale);
                  setIsOpen(false);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: tokens.borderRadius.sm,
                  backgroundColor:
                    value === scale
                      ? tokens.colors.accent.primary
                      : "transparent",
                  color:
                    value === scale ? "#ffffff" : tokens.colors.text.secondary,
                  fontSize: "0.6875rem", // 11px in rem
                  fontWeight: value === scale ? 500 : 400, // AstroUXDS: 500 (medium) not 600
                  fontFamily: tokens.typography.fontFamily.mono,
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.1s ease",
                  ...tabularNumsStyle,
                }}
                onMouseEnter={(e) => {
                  if (value !== scale) {
                    e.currentTarget.style.backgroundColor =
                      "rgba(255,255,255,0.1)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    value === scale
                      ? tokens.colors.accent.primary
                      : "transparent";
                }}
              >
                {formatTimeScale(scale)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
});

interface SimulationTimeDisplayProps {
  currentTime?: Date;
  elapsedSeconds?: number;
  showElapsed?: boolean;
  showCurrent?: boolean;
  showDate?: boolean;
  isPlaying?: boolean;
  tokens: ReturnType<typeof useTheme>["tokens"];
  small?: boolean;
}

const SimulationTimeDisplay = memo(function SimulationTimeDisplay({
  currentTime,
  elapsedSeconds,
  showElapsed = true,
  showCurrent = true,
  showDate = false,
  isPlaying = false,
  tokens,
  small = false,
}: SimulationTimeDisplayProps) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      }}
    >
      {showDate && currentTime && (
        <span
          style={{
            fontSize: tokens.typography.fontSize.xs,
            fontFamily: tokens.typography.fontFamily.mono,
            color: tokens.colors.text.tertiary,
            ...tabularNumsStyle,
          }}
        >
          {formatDateDisplay(currentTime)}
        </span>
      )}

      {showCurrent && currentTime && (
        <span
          style={{
            fontSize: small
              ? tokens.typography.fontSize.lg
              : tokens.typography.fontSize.xl,
            fontWeight: tokens.typography.fontWeight.bold, // Bold (700) to match MissionClock
            fontFamily: tokens.typography.fontFamily.mono,
            color: tokens.colors.text.primary,
            ...tabularNumsStyle,
          }}
        >
          {formatTimeDisplay(currentTime)}
        </span>
      )}

      {showElapsed && elapsedSeconds !== undefined && (
        <span
          style={{
            fontSize: tokens.typography.fontSize.sm,
            fontFamily: tokens.typography.fontFamily.mono,
            color: tokens.colors.status.caution,
            ...tabularNumsStyle,
          }}
        >
          T{elapsedSeconds >= 0 ? "+" : ""}
          {formatElapsedTime(elapsedSeconds)}
        </span>
      )}

      <span
        style={{
          fontSize: tokens.typography.fontSize.xs,
          fontWeight: tokens.typography.fontWeight.medium,
          color: isPlaying
            ? safeAccentText(tokens.colors.accent.primary)
            : tokens.colors.status.standby,
          textTransform: "uppercase",
          letterSpacing: tokens.typography.letterSpacing.wide,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {isPlaying && (
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: tokens.colors.accent.primary,
              animation: "pulse 1.5s ease-in-out infinite",
            }}
          />
        )}
        SIM
      </span>
    </div>
  );
});

// ============================================================================
// Main Component
// ============================================================================

/**
 * SimulationControls - Playback controls for space simulation
 *
 * Provides comprehensive simulation time control following Astro UX patterns.
 * Designed for mission control and space operations applications.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <SimulationControls
 *   isPlaying={isPlaying}
 *   onPlay={() => setIsPlaying(true)}
 *   onPause={() => setIsPlaying(false)}
 *   onStop={handleReset}
 * />
 *
 * // Full featured
 * <SimulationControls
 *   isPlaying={isPlaying}
 *   onPlay={handlePlay}
 *   onPause={handlePause}
 *   onStop={handleStop}
 *   onStepForward={handleStepForward}
 *   onStepBackward={handleStepBackward}
 *   currentTime={simulationTime}
 *   epoch={epoch}
 *   elapsedSeconds={elapsed}
 *   timeScale={timeScale}
 *   onTimeScaleChange={setTimeScale}
 *   showTimeScale
 *   showStepControls
 *   showElapsedTime
 * />
 *
 * // In AppBar center slot
 * <AppBar
 *   appName="Mission Control"
 *   centerSlot={
 *     <SimulationControls
 *       compact
 *       isPlaying={isPlaying}
 *       onPlay={handlePlay}
 *       onPause={handlePause}
 *       currentTime={simTime}
 *     />
 *   }
 * />
 * ```
 */
export const SimulationControls = memo(function SimulationControls({
  state,
  isPlaying: isPlayingProp,
  isPaused,
  onPlay,
  onPause,
  onStop,
  onStepForward,
  onStepBackward,
  currentTime: currentTimeProp,
  epoch,
  elapsedSeconds: elapsedSecondsProp,
  showElapsedTime = true,
  showCurrentTime = true,
  showDate = true,
  timeScale = 1,
  onTimeScaleChange,
  timeScalePresets = DEFAULT_TIME_SCALE_PRESETS,
  showTimeScale = true,
  stepSize = 60,
  onStepSizeChange: _onStepSizeChange,
  stepSizePresets: _stepSizePresets = DEFAULT_STEP_SIZE_PRESETS,
  showStepControls = true,
  compact = false,
  small = false,
  className = "",
  disabled = false,
  showStatus = true,
  label,
}: SimulationControlsProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";

  // Derive playing state from props
  const isPlaying = useMemo(() => {
    if (state) return state === "playing";
    return isPlayingProp ?? false;
  }, [state, isPlayingProp]);

  const isStopped = useMemo(() => {
    if (state) return state === "stopped";
    return !isPlayingProp && !isPaused;
  }, [state, isPlayingProp, isPaused]);

  // Parse current time
  const currentTime = useMemo(() => {
    if (!currentTimeProp) return undefined;
    return typeof currentTimeProp === "string"
      ? new Date(currentTimeProp)
      : currentTimeProp;
  }, [currentTimeProp]);

  // Calculate elapsed seconds
  const elapsedSeconds = useMemo(() => {
    if (elapsedSecondsProp !== undefined) return elapsedSecondsProp;
    if (currentTime && epoch) {
      const epochDate = typeof epoch === "string" ? new Date(epoch) : epoch;
      return (currentTime.getTime() - epochDate.getTime()) / 1000;
    }
    return undefined;
  }, [elapsedSecondsProp, currentTime, epoch]);

  // Handle play/pause toggle
  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      onPause?.();
    } else {
      onPlay?.();
    }
  }, [isPlaying, onPlay, onPause]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      switch (e.key) {
        case " ":
          e.preventDefault();
          handlePlayPause();
          break;
        case "Escape":
          e.preventDefault();
          onStop?.();
          break;
        case "ArrowRight":
          e.preventDefault();
          onStepForward?.();
          break;
        case "ArrowLeft":
          e.preventDefault();
          onStepBackward?.();
          break;
      }
    },
    [disabled, handlePlayPause, onStop, onStepForward, onStepBackward],
  );

  return (
    <div
      className={classNames("zendir-simulation-controls", className)}
      role="group"
      aria-label={label || "Simulation Controls"}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      style={{
        display: "flex",
        alignItems: "center",
        gap: compact ? tokens.spacing.sm : tokens.spacing.md,
        padding: compact
          ? `${tokens.spacing.xs} ${tokens.spacing.sm}`
          : `${tokens.spacing.sm} ${tokens.spacing.md}`,
        backgroundColor: isTransparentTheme
          ? "rgba(0,0,0,0.3)"
          : tokens.colors.background.surface,
        ...(isTransparentTheme && {
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)" as const,
        }),
        ...(tokens.colors.border.cardStyle ?? {
          border: `1px solid ${tokens.colors.border.muted}`,
        }),
        borderRadius: tokens.borderRadius.lg,
        outline: "none",
        transition: tokens.animation.fast,
      }}
    >
      {/* Time Display (if showing time) */}
      {(showCurrentTime || showElapsedTime) && !compact && (
        <SimulationTimeDisplay
          currentTime={currentTime}
          elapsedSeconds={elapsedSeconds}
          showElapsed={showElapsedTime}
          showCurrent={showCurrentTime}
          showDate={showDate}
          isPlaying={isPlaying}
          tokens={tokens}
          small={small}
        />
      )}

      {/* Compact time display */}
      {compact && currentTime && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {showDate && (
            <span
              style={{
                fontSize: tokens.typography.fontSize.xs,
                fontFamily: tokens.typography.fontFamily.mono,
                color: tokens.colors.text.tertiary,
                ...tabularNumsStyle,
              }}
            >
              {formatDateDisplay(currentTime)}
            </span>
          )}
          <span
            style={{
              fontSize: tokens.typography.fontSize.lg,
              fontWeight: tokens.typography.fontWeight.bold, // Bold (700) to match MissionClock
              fontFamily: tokens.typography.fontFamily.mono,
              color: tokens.colors.text.primary,
              ...tabularNumsStyle,
            }}
          >
            {formatTimeDisplay(currentTime)}
          </span>
          {showStatus && (
            <span
              style={{
                fontSize: tokens.typography.fontSize.xs,
                fontWeight: tokens.typography.fontWeight.medium,
                color: isPlaying
                  ? safeAccentText(tokens.colors.accent.primary)
                  : tokens.colors.status.standby,
                textTransform: "uppercase",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {isPlaying && (
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: tokens.colors.accent.primary,
                  }}
                />
              )}
              SIM
            </span>
          )}
        </div>
      )}

      {/* Separator */}
      {(showCurrentTime || showElapsedTime || (compact && currentTime)) && (
        <div
          style={{
            width: 1,
            height: 24,
            backgroundColor: tokens.colors.border.muted,
          }}
        />
      )}

      {/* Transport Controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {/* Step Backward */}
        {showStepControls && (
          <ControlButton
            icon="skip-previous"
            label={`Step backward ${formatStepSize(stepSize)}`}
            onClick={onStepBackward}
            disabled={disabled || !onStepBackward}
            tokens={tokens}
            small={small}
          />
        )}

        {/* Stop */}
        <ControlButton
          icon="stop"
          label="Stop / Reset"
          onClick={onStop}
          disabled={disabled || !onStop}
          danger={!isStopped}
          tokens={tokens}
          small={small}
        />

        {/* Play/Pause */}
        <Tooltip
          content={isPlaying ? "Pause (Space)" : "Play (Space)"}
          placement="bottom"
        >
          <button
            onClick={handlePlayPause}
            disabled={disabled}
            aria-label={isPlaying ? "Pause simulation" : "Play simulation"}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: small ? 36 : 44,
              height: small ? 36 : 44,
              padding: 0,
              border: isPlaying
                ? `2px solid ${tokens.colors.accent.primary}`
                : "none",
              borderRadius: "50%",
              // Use accent primary for both states - playing has outline style
              backgroundColor: isPlaying
                ? "rgba(138, 43, 226, 0.15)" // Subtle purple fill when playing
                : tokens.colors.accent.primary,
              color: isPlaying
                ? safeAccentText(tokens.colors.accent.primary)
                : "#ffffff",
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "all 0.15s ease",
              opacity: disabled ? 0.5 : 1,
              boxShadow: isPlaying
                ? `0 0 16px ${tokens.colors.accent.primary}50`
                : `0 0 8px ${tokens.colors.accent.primary}40`,
            }}
            onMouseEnter={(e) => {
              if (!disabled) {
                e.currentTarget.style.transform = "scale(1.05)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <AstroIcon
              name={isPlaying ? "pause" : "play-arrow"}
              size={small ? 18 : 22}
              color={isPlaying ? tokens.colors.accent.primary : "#ffffff"}
            />
          </button>
        </Tooltip>

        {/* Step Forward */}
        {showStepControls && (
          <ControlButton
            icon="skip-next"
            label={`Step forward ${formatStepSize(stepSize)}`}
            onClick={onStepForward}
            disabled={disabled || !onStepForward}
            tokens={tokens}
            small={small}
          />
        )}
      </div>

      {/* Time Scale */}
      {showTimeScale && (
        <>
          <div
            style={{
              width: 1,
              height: 24,
              backgroundColor: tokens.colors.border.muted,
            }}
          />
          <TimeScaleSelector
            value={timeScale}
            presets={timeScalePresets}
            onChange={onTimeScaleChange}
            disabled={disabled || !onTimeScaleChange}
            tokens={tokens}
            small={small}
          />
        </>
      )}
    </div>
  );
});

// ============================================================================
// Convenience Components
// ============================================================================

/**
 * MiniSimulationControls - Ultra-compact controls for AppBar
 */
export interface MiniSimulationControlsProps {
  isPlaying?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  onStop?: () => void;
  timeScale?: number;
  onTimeScaleChange?: (scale: number) => void;
  disabled?: boolean;
  className?: string;
}

export const MiniSimulationControls = memo(function MiniSimulationControls({
  isPlaying = false,
  onPlay,
  onPause,
  onStop,
  timeScale = 1,
  onTimeScaleChange,
  disabled = false,
  className = "",
}: MiniSimulationControlsProps): React.ReactElement {
  return (
    <SimulationControls
      isPlaying={isPlaying}
      onPlay={onPlay}
      onPause={onPause}
      onStop={onStop}
      timeScale={timeScale}
      onTimeScaleChange={onTimeScaleChange}
      disabled={disabled}
      compact
      small
      showCurrentTime={false}
      showElapsedTime={false}
      showDate={false}
      showStepControls={false}
      showTimeScale={Boolean(onTimeScaleChange)}
      className={className}
    />
  );
});

/**
 * SimulationControlsWithClock - Controls combined with MissionClock
 */
export interface SimulationControlsWithClockProps extends Omit<
  SimulationControlsProps,
  "showCurrentTime" | "showElapsedTime"
> {
  /** Clock format */
  clockFormat?: "12h" | "24h";
  /** Timezone label */
  timezone?: string;
}

export const SimulationControlsWithClock = memo(
  function SimulationControlsWithClock({
    clockFormat: _clockFormat = "24h",
    timezone = "SIM",
    ...props
  }: SimulationControlsWithClockProps): React.ReactElement {
    return (
      <SimulationControls
        {...props}
        showCurrentTime
        showElapsedTime
        showDate
        label={`Simulation Controls - ${timezone}`}
      />
    );
  },
);

export default SimulationControls;
